import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Plus, Eye, CheckCircle, AlertTriangle, 
  Loader2, ChevronLeft, ChevronRight, Phone, MessageCircle,
  Clock, User, X
} from 'lucide-react';
import { formatDate } from '../lib/utils';

const Complaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [problemTypes, setProblemTypes] = useState([]);
  const [problemTypeFilter, setProblemTypeFilter] = useState('');
  const [solvers, setSolvers] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSolveModal, setShowSolveModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [solutionRemarks, setSolutionRemarks] = useState('');
  const [selectedSolver, setSelectedSolver] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  const user = authService.getCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Apply filters from navigation state
    if (location.state?.statusFilter) {
      setStatusFilter(location.state.statusFilter);
    }
    if (location.state?.escalated) {
      setStatusFilter('escalated');
    }
  }, [location.state]);

  useEffect(() => {
    fetchComplaints();
    fetchProblemTypes();
    if (['admin', 'manager'].includes(user?.role)) {
      fetchSolvers();
    }
  }, [page, statusFilter, sourceFilter, problemTypeFilter, search]);

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (problemTypeFilter) params.problem_type = problemTypeFilter;
      if (search) params.search = search;
      
      const response = await api.getComplaints(params);
      // API returns array directly, not paginated object
      const data = response.data;
      if (Array.isArray(data)) {
        setComplaints(data);
        setTotal(data.length);
        setTotalPages(1);
      } else {
        setComplaints(data.complaints || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      toast.error('Failed to fetch complaints');
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProblemTypes = async () => {
    try {
      const response = await api.getProblemTypes();
      setProblemTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch problem types:', error);
    }
  };

  const fetchSolvers = async () => {
    try {
      const response = await api.getComplaintSolvers();
      setSolvers(response.data);
    } catch (error) {
      console.error('Failed to fetch solvers:', error);
    }
  };

  const handleSolve = async () => {
    if (!solutionRemarks.trim()) {
      toast.error('Please enter solution remarks');
      return;
    }
    setActionLoading(true);
    try {
      await api.solveComplaint(selectedComplaint.id, solutionRemarks);
      toast.success('Complaint marked as solved');
      setShowSolveModal(false);
      setSolutionRemarks('');
      fetchComplaints();
    } catch (error) {
      toast.error('Failed to solve complaint');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEscalate = async (complaint) => {
    if (!window.confirm('Are you sure you want to escalate this complaint to admin?')) return;
    try {
      await api.escalateComplaint(complaint.id);
      toast.success('Complaint escalated to admin');
      fetchComplaints();
    } catch (error) {
      toast.error('Failed to escalate complaint');
    }
  };

  const handleAssign = async () => {
    if (!selectedSolver) {
      toast.error('Please select a solver');
      return;
    }
    setActionLoading(true);
    try {
      await api.assignComplaint(selectedComplaint.id, selectedSolver);
      toast.success('Complaint assigned successfully');
      setShowAssignModal(false);
      setSelectedSolver('');
      fetchComplaints();
    } catch (error) {
      toast.error('Failed to assign complaint');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-amber-100 text-amber-700',
      solved: 'bg-green-100 text-green-700',
      escalated: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  const getSourceIcon = (source) => {
    if (source === 'calling') return <Phone size={14} className="text-blue-600" />;
    return <MessageCircle size={14} className="text-green-600" />;
  };

  return (
    <div data-testid="complaints-list" className="max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-headings text-slate-900">Complaints</h1>
          <p className="text-slate-600 font-body">{total} total complaints</p>
        </div>
        {['admin', 'manager', 'complaint_entry'].includes(user?.role) && (
          <button
            onClick={() => navigate('/complaints/new')}
            data-testid="new-complaint-btn"
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Plus size={20} />
            New Complaint
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative md:col-span-2">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, phone, ticket..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="solved">Solved</option>
            <option value="escalated">Escalated</option>
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="">All Sources</option>
            <option value="calling">Calling</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          <select
            value={problemTypeFilter}
            onChange={(e) => setProblemTypeFilter(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="">All Problem Types</option>
            {problemTypes.map(pt => (
              <option key={pt.id} value={pt.name}>{pt.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : complaints.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <AlertTriangle size={48} className="mb-4 text-slate-300" />
            <p>No complaints found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Ticket</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Student</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Problem</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Timeline</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Assigned To</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {complaints.map((complaint) => (
                    <tr key={complaint.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm font-medium text-orange-600">
                          {complaint.ticket_number}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">{complaint.student_name}</p>
                          <p className="text-sm text-slate-500 font-mono">{complaint.phone}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-700">{complaint.problem_type}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {getSourceIcon(complaint.source)}
                          <span className="text-sm capitalize">{complaint.source}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(complaint.status)}
                        {complaint.escalated && complaint.status !== 'escalated' && (
                          <AlertTriangle size={14} className="inline ml-1 text-red-500" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Clock size={14} />
                          {formatDate(complaint.timeline)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {complaint.assigned_to_name ? (
                          <div className="flex items-center gap-1 text-sm">
                            <User size={14} className="text-slate-400" />
                            {complaint.assigned_to_name}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">Unassigned</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => { setSelectedComplaint(complaint); setShowDetailModal(true); }}
                            className="p-1.5 text-slate-600 hover:bg-slate-100 rounded"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          {complaint.status !== 'solved' && ['admin', 'manager', 'complaint_solver'].includes(user?.role) && (
                            <>
                              {(user?.role !== 'complaint_solver' || complaint.assigned_to === user.id) && (
                                <button
                                  onClick={() => { setSelectedComplaint(complaint); setShowSolveModal(true); }}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                                  title="Mark Solved"
                                >
                                  <CheckCircle size={16} />
                                </button>
                              )}
                              {!complaint.escalated && user?.role === 'complaint_solver' && (
                                <button
                                  onClick={() => handleEscalate(complaint)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                  title="Escalate"
                                >
                                  <AlertTriangle size={16} />
                                </button>
                              )}
                            </>
                          )}
                          {['admin', 'manager'].includes(user?.role) && complaint.status !== 'solved' && (
                            <button
                              onClick={() => { setSelectedComplaint(complaint); setShowAssignModal(true); }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="Assign"
                            >
                              <User size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Page {page} of {totalPages} ({total} total)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 border rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 border rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold font-headings">
                Complaint Details - {selectedComplaint.ticket_number}
              </h2>
              <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold">Student Name</p>
                  <p className="font-medium">{selectedComplaint.student_name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold">Phone</p>
                  <p className="font-mono">{selectedComplaint.phone}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold">Problem Type</p>
                  <p>{selectedComplaint.problem_type}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold">Source</p>
                  <p className="capitalize">{selectedComplaint.source}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold">Status</p>
                  {getStatusBadge(selectedComplaint.status)}
                </div>
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold">Timeline</p>
                  <p>{formatDate(selectedComplaint.timeline)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold">Created By</p>
                  <p>{selectedComplaint.created_by_name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold">Assigned To</p>
                  <p>{selectedComplaint.assigned_to_name || 'Unassigned'}</p>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500 font-bold mb-2">Remarks</p>
                <p className="bg-slate-50 p-3 rounded-lg">{selectedComplaint.remarks}</p>
              </div>
              {selectedComplaint.solution_remarks && (
                <div>
                  <p className="text-xs uppercase text-slate-500 font-bold mb-2">Solution</p>
                  <p className="bg-green-50 p-3 rounded-lg text-green-800">{selectedComplaint.solution_remarks}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm text-slate-500">
                <div>Created: {formatDate(selectedComplaint.created_at)}</div>
                {selectedComplaint.solved_at && <div>Solved: {formatDate(selectedComplaint.solved_at)}</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Solve Modal */}
      {showSolveModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold font-headings">Mark as Solved</h2>
              <p className="text-sm text-slate-500">Ticket: {selectedComplaint.ticket_number}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Solution Remarks *</label>
                <textarea
                  value={solutionRemarks}
                  onChange={(e) => setSolutionRemarks(e.target.value)}
                  placeholder="Describe how the complaint was resolved..."
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 h-32"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setShowSolveModal(false); setSolutionRemarks(''); }}
                  className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSolve}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading && <Loader2 size={16} className="animate-spin" />}
                  Mark Solved
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold font-headings">Assign Complaint</h2>
              <p className="text-sm text-slate-500">Ticket: {selectedComplaint.ticket_number}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Solver *</label>
                <select
                  value={selectedSolver}
                  onChange={(e) => setSelectedSolver(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a solver...</option>
                  {solvers.map(solver => (
                    <option key={solver.id} value={solver.id}>{solver.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setShowAssignModal(false); setSelectedSolver(''); }}
                  className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssign}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {actionLoading && <Loader2 size={16} className="animate-spin" />}
                  Assign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Complaints;
